/**
 * React Query hooks for Admin User Management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchUsers,
  fetchUser,
  createUser,
  updateUser,
  deleteUser,
  adminResetPassword,
  deactivateUser,
  reactivateUser,
} from '@/lib/users-api'
import { isApiError } from '@/lib/api-client'
import type {
  ListUsersParams,
  CreateUserRequest,
  UpdateUserRequest,
  AdminResetPasswordRequest,
} from '@/types/user-management'

// ============================================================================
// QUERY KEYS
// ============================================================================

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: ListUsersParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Fetch paginated, filtered list of users
 */
export function useUsers(params: ListUsersParams = {}) {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => fetchUsers(params),
  })
}

/**
 * Fetch a single user by ID
 */
export function useUser(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => fetchUser(id),
    enabled: !!id,
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Create a new platform user
 */
export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateUserRequest) => createUser(data),
    onSuccess: (newUser) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      toast.success(`User "${newUser.email}" created successfully`)
    },
    onError: (error) => {
      if (isApiError(error)) {
        toast.error(error.message || 'Failed to create user')
      } else {
        toast.error('Failed to create user')
      }
    },
  })
}

/**
 * Update a user's details
 */
export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      updateUser(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(userKeys.detail(updated.id), updated)
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      toast.success('User updated successfully')
    },
    onError: (error) => {
      if (isApiError(error)) {
        toast.error(error.message || 'Failed to update user')
      } else {
        toast.error('Failed to update user')
      }
    },
  })
}

/**
 * Delete a user permanently
 */
export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: userKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      toast.success('User deleted successfully')
    },
    onError: (error) => {
      if (isApiError(error)) {
        toast.error(error.message || 'Failed to delete user')
      } else {
        toast.error('Failed to delete user')
      }
    },
  })
}

/**
 * Admin: reset a user's password
 */
export function useAdminResetPassword() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdminResetPasswordRequest }) =>
      adminResetPassword(id, data),
    onSuccess: () => {
      toast.success('Password reset successfully')
    },
    onError: (error) => {
      if (isApiError(error)) {
        toast.error(error.message || 'Failed to reset password')
      } else {
        toast.error('Failed to reset password')
      }
    },
  })
}

/**
 * Deactivate a user account
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deactivateUser(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(userKeys.detail(updated.id), updated)
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      toast.success('User deactivated')
    },
    onError: (error) => {
      if (isApiError(error)) {
        toast.error(error.message || 'Failed to deactivate user')
      } else {
        toast.error('Failed to deactivate user')
      }
    },
  })
}

/**
 * Reactivate a user account
 */
export function useReactivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => reactivateUser(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(userKeys.detail(updated.id), updated)
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      toast.success('User reactivated')
    },
    onError: (error) => {
      if (isApiError(error)) {
        toast.error(error.message || 'Failed to reactivate user')
      } else {
        toast.error('Failed to reactivate user')
      }
    },
  })
}
